/**
 * Where everything stands on the floor.
 *
 * Every position in the scene is derived from two numbers — how many developers there are, and how
 * big the floor is — and from nothing else. No developer has a hardcoded coordinate, because the
 * moment one does, the fifth developer to join an organization appears on top of the fourth.
 *
 * Coordinates are in a fixed design space (1000 wide) and the scene scales as a whole through the
 * SVG viewBox. That means layout arithmetic never has to know the pixel size of the viewport, and
 * a station is the same shape on a laptop and a wall display.
 */

/**
 * The design space, in width. Height is measured from the container.
 *
 * Fixing both dimensions was a mistake worth naming: a 1000x620 box inside a 970x810 stage is
 * letterboxed by `meet`, so the room floated in the middle of two empty bands and every figure was
 * scaled down to fit a height it was not using. The width stays fixed so layout arithmetic is
 * stable; the height follows the space the page actually gives.
 */
export const DESIGN_WIDTH = 1000

/** Clamped so an extreme window does not produce a corridor or a postage stamp. */
export const MIN_DESIGN_HEIGHT = 560
export const MAX_DESIGN_HEIGHT = 900

export interface FloorSize {
  width: number
  height: number
}

/** The design-space height for a stage of this pixel aspect. */
export function designHeightFor(pixelWidth: number, pixelHeight: number): number {
  if (pixelWidth <= 0) return 640

  const height = DESIGN_WIDTH * (pixelHeight / pixelWidth)

  return Math.round(Math.min(MAX_DESIGN_HEIGHT, Math.max(MIN_DESIGN_HEIGHT, height)))
}

/**
 * Where the back wall meets the floor.
 *
 * Two fifths of the height: the wall carries the windows, the sign and the boards, and the figures
 * stand on the floor in front of it with their heads against the wall, which is what gives the
 * room depth without any perspective arithmetic.
 */
export function horizonFor(size: FloorSize): number {
  // A room that grew for a big team grows its floor, not its wall: past the tallest window-shaped
  // room the wall stops where it was, and the extra depth is all floor to stand on.
  return Math.round(Math.min(size.height, MAX_DESIGN_HEIGHT) * 0.4)
}

/** The containment jar's footprint, reserved before stations are placed. It stands on the floor. */
export function containmentBox(size: FloorSize) {
  const width = 196
  const height = Math.min(300, size.height * 0.48)

  return {
    x: size.width - width - 34,
    y: size.height - 30 - height,
    width,
    height,
  }
}

export interface Station {
  developerId: string
  /** Where the developer's feet are. Everything at the station is drawn relative to this. */
  x: number
  y: number
  /** How large this station is drawn, 1 being the reference size. */
  scale: number
  /** Row index, used for draw order so nearer stations overlap further ones. */
  row: number
  /**
   * How large the card over the person is drawn, independent of the figure's scale. A card that
   * shrinks with the grid stops being readable exactly when the floor is busiest, and one that
   * never shrinks overlaps its neighbours once there are four to a row. So: full size until the
   * column is narrower than the card, then exactly the column.
   */
  cardScale: number
}

/** The width of the card over each person, in design units at full size. */
export const CARD_WIDTH = 150

export interface FloorLayout {
  stations: Station[]
  columns: number
  rows: number
  scale: number
}

/**
 * Arranges stations for however many developers there are.
 *
 * The brief asks for larger stations when there are few and a denser grid when there are many,
 * which is the opposite of what a fixed grid does. The column count grows with the square root of
 * the population so that the floor fills in both directions rather than becoming a long line, and
 * the scale falls with it — but only to a floor, because a station nobody can read is not a
 * smaller station, it is a missing one.
 *
 * Rows further back are drawn slightly smaller. That one factor is most of what makes a grid of
 * people read as a room seen from the door.
 */
/**
 * @param bounds The strip of the floor to use, left to right. The Bug Hunt keeps clear of its
 * counters and its jar; an area of the room has its own edges.
 */
export function computeFloor(
  developerIds: string[],
  size: FloorSize,
  bounds?: { left: number; right: number },
): FloorLayout {
  const count = Math.max(1, developerIds.length)
  const horizon = horizonFor(size)
  const { left, usableWidth } = stripOf(size, bounds)

  // Feet stay on the floor: the back row far enough below the wall that its desks are on the
  // floor too, the front row far enough above the edge that nobody's shoes are cut off.
  const floorTop = horizon + FLOOR_TOP_GAP
  const floorBottom = size.height - FLOOR_BOTTOM_GAP

  const { columns, rows, byGrid } = gridFor(count, usableWidth)

  const cellWidth = usableWidth / columns
  const rowGap = rows > 1 ? (floorBottom - floorTop) / (rows - 1) : 0

  // Bounded by the columns and by the distance between rows, so a front-row figure never stands
  // on the head of the one behind it.
  const byRows = rows > 1 ? rowGap / ROW_PITCH : Infinity
  // The floor of 0.6 was the point where a crowd stopped fitting rather than the point where a
  // figure stops reading. A crowd's room grows instead (roomHeightFor), so its figures can stay at
  // a size that still reads as a character.
  const scale = Math.max(count > ROOM_HOLDS ? CROWD_SCALE : 0.6, Math.min(1.3, byGrid, byRows))

  const stations = developerIds.map((developerId, index) => {
    const column = index % columns
    const row = Math.floor(index / columns)

    // Odd rows are nudged sideways. A perfect grid reads as a spreadsheet; a slight stagger reads
    // as a room, and it also keeps a station from hiding directly behind the one in front.
    const stagger = row % 2 === 1 ? cellWidth * 0.14 : 0
    const depth = rows > 1 ? 0.86 + 0.14 * (row / (rows - 1)) : 1

    return {
      developerId,
      x: left + cellWidth * column + cellWidth / 2 + stagger,
      y: rows > 1 ? floorTop + rowGap * row : floorTop + (floorBottom - floorTop) * 0.62,
      scale: scale * depth,
      row,
      cardScale: Math.min(1, (cellWidth - 8) / CARD_WIDTH),
    }
  })

  return { stations, columns, rows, scale }
}

/**
 * Up to this many figures a floor is the shape of the window. Past it the room grows (owner,
 * 10/10/2026: "for a big team it can get small — make a bigger version"): deeper, with more rows,
 * rather than the same floor with everybody shrunk onto each other's heads.
 */
export const ROOM_HOLDS = 16

/** The smallest a figure is drawn in a crowd's room. Below it a character stops reading as one. */
export const CROWD_SCALE = 0.5

/**
 * The narrowest column a station gets: a person, their agent and a clone side by side at
 * {@link CROWD_SCALE}. A wide room keeps more columns; a narrow one adds rows and grows deeper.
 */
export const MIN_CELL = 120

/** Rows stand a figure's height apart at scale 1, so nobody stands on the head of the one behind. */
const ROW_PITCH = 150
const FLOOR_TOP_GAP = 96
const FLOOR_BOTTOM_GAP = 36

/** The strip of floor the stations use: clear of the counters on the left and the jar on the right. */
function stripOf(size: FloorSize, bounds?: { left: number; right: number }): { left: number; usableWidth: number } {
  const left = bounds?.left ?? 120
  return { left, usableWidth: (bounds?.right ?? containmentBox(size).x - 40) - left }
}

/**
 * Columns and rows for a crowd. Columns keep growing with it — somebody running a team of agents
 * alongside their colleagues fills a floor quickly, and a fixed five columns turned that into a
 * queue down the room — but never narrower than {@link MIN_CELL}: past that, more rows.
 */
function gridFor(count: number, usableWidth: number): { columns: number; rows: number; byGrid: number } {
  const wanted =
    count <= 2 ? count
      : count <= 4 ? 2
        : count <= 9 ? 3
          : count <= 16 ? 4
            : count <= 25 ? 5
              : count <= 36 ? 6
                : count <= 49 ? 7
                  : 8
  const columns = Math.max(1, Math.min(wanted, Math.floor(usableWidth / MIN_CELL)))
  const rows = Math.ceil(count / columns)
  return { columns, rows, byGrid: 2.7 / Math.max(columns, rows) }
}

/**
 * How tall the room is drawn for this many figures on this strip of it: the window's height up to
 * {@link ROOM_HOLDS}, and past it as deep as the rows need to stand a figure's height apart.
 *
 * Only the floor grows (horizonFor stops the wall), so 60 people in one area make a room about
 * 1100 units deep where 16 fit in the window's 640: everybody visible at "fit", and the camera's
 * zoom gets close to any of them.
 */
export function roomHeightFor(count: number, size: FloorSize, bounds?: { left: number; right: number }): number {
  if (count <= ROOM_HOLDS) return size.height

  const { rows, byGrid } = gridFor(count, stripOf(size, bounds).usableWidth)
  const scale = Math.max(CROWD_SCALE, Math.min(1.3, byGrid))
  const depth = (rows - 1) * ROW_PITCH * scale + FLOOR_TOP_GAP + FLOOR_BOTTOM_GAP

  // While the room is no taller than MAX_DESIGN_HEIGHT the wall is two fifths of it, so the floor
  // is three fifths; past that the wall is fixed and every extra unit is floor.
  const withinCap = depth / 0.6
  const needed = withinCap <= MAX_DESIGN_HEIGHT ? withinCap : depth + MAX_DESIGN_HEIGHT * 0.4

  return Math.max(size.height, Math.ceil(needed))
}

/**
 * Where a bug is while it is being worked on, relative to its station.
 *
 * In the air beside the racket, at head height, fanned out so a developer with three bugs does not
 * have them stacked in one place.
 */
export function bugSlotAt(station: Station, index: number): { x: number; y: number } {
  return {
    x: station.x + (76 + index * 28) * station.scale,
    y: station.y - (96 - (index % 2) * 30) * station.scale,
  }
}

/**
 * Where the AI companion hovers when an agent is working at this station.
 *
 * Spaced by more than a companion is wide. The first version used 26 units between them and each
 * one is 26 across, so three concurrent agents drew as one overlapping smear.
 */
export function agentSlotAt(station: Station, index: number): { x: number; y: number } {
  return {
    x: station.x - (58 + index * 34) * station.scale,
    y: station.y - (112 + (index % 2) * 14) * station.scale,
  }
}

/**
 * A person running several agents at once gets a clone at their station for each agent past the
 * first: "2_Alice" is Alice's second agent, "3_Alice" her third. Past {@link MAX_CLONES} the rest
 * become a count.
 */
export const MAX_CLONES = 3

/**
 * Past this many agents at work in the room, it is crowded (docs/refactor/25, phase 4): the lines from people to their
 * agents are drawn only for whoever is selected, or the floor turns into a web.
 */
export const CROWDED_AGENTS = 12

/** Whether the room is crowded: the agents at work, counted over everyone in it. */
export function isCrowded(developers: Iterable<{ sessions: ReadonlyArray<{ activityState: string }> }>): boolean {
  let agents = 0
  for (const developer of developers) agents += developer.sessions.filter((session) => session.activityState !== 'Idle').length
  return agents > CROWDED_AGENTS
}

/** The name a clone carries: its agent's number, then the owner's name. */
export function cloneName(agentNumber: number, owner: string): string {
  return `${agentNumber}_${owner}`
}

/** Where the n-th clone (0 = the person's second agent) stands, beside the person, in station units. */
export function cloneSpotAt(index: number): { x: number; y: number } {
  return { x: 70 + index * 48, y: 4 + (index % 2) * 6 }
}

/** How many bugs the jar draws before the rest become a count. */
export const JAR_CELLS = 8

/**
 * One of the jar's eight spots: two columns of four, with a little jitter from the bug's id so
 * the jar does not look like a tray.
 *
 * Cells rather than free positions because free positions drawn from ids collide: two bugs whose
 * ids hash close together sat on top of each other, labels and all.
 */
export function jarCell(cell: number, seed: number, size: FloorSize): { x: number; y: number } {
  const tank = containmentBox(size)
  const insetX = 40
  const top = tank.y + 64
  const width = tank.width - insetX * 2
  const height = tank.height - 64 - 58

  const column = cell % 2
  const row = Math.floor(cell / 2) % (JAR_CELLS / 2)

  // Two independent pseudo-random draws from the same seed.
  const a = ((seed * 9301 + 49297) % 233280) / 233280
  const b = ((seed * 4801 + 19813) % 233280) / 233280

  return {
    x: tank.x + insetX + ((column + 0.5) * width) / 2 + (a - 0.5) * 20,
    y: top + ((row + 0.5) * height) / (JAR_CELLS / 2) + (b - 0.5) * 12,
  }
}

/**
 * Gives each bug in the jar a cell.
 *
 * A bug prefers the cell its own id picks, so it stays where it is while others arrive and leave;
 * only a collision moves it, and then to the next free cell. Stable where it can be, never
 * overlapping.
 */
export function assignJarCells(seeds: number[]): number[] {
  const taken = new Set<number>()

  return seeds.map((seed) => {
    let cell = seed % JAR_CELLS
    while (taken.has(cell) && taken.size < JAR_CELLS) cell = (cell + 1) % JAR_CELLS
    taken.add(cell)
    return cell
  })
}

/** A small integer from a string, for deterministic variation that survives a reload. */
export function seedFrom(value: string): number {
  let hash = 2166136261

  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }

  return Math.abs(hash)
}

/**
 * A line of chat as a speech bubble's lines: words kept whole where they fit, a long one broken,
 * and at most `maxLines` of them — the last ending in an ellipsis when the text runs on.
 */
export function wrapSpeech(text: string, perLine = 28, maxLines = 4): string[] {
  const lines: string[] = []
  let current = ''
  for (const word of text.split(/\s+/).filter(Boolean)) {
    let rest = word
    while (rest.length > perLine) {
      if (current) {
        lines.push(current)
        current = ''
      }
      lines.push(rest.slice(0, perLine))
      rest = rest.slice(perLine)
    }
    if (!current) current = rest
    else if (current.length + 1 + rest.length <= perLine) current += ` ${rest}`
    else {
      lines.push(current)
      current = rest
    }
  }
  if (current) lines.push(current)
  if (lines.length <= maxLines) return lines
  const kept = lines.slice(0, maxLines)
  const last = kept[maxLines - 1] ?? ''
  kept[maxLines - 1] = `${last.slice(0, perLine - 1).trimEnd()}…`
  return kept
}
